/**
 * Zero-floor category guard (vague-intake trace, 2026-07-30 - PROC-000026
 * family): intake_agent's own category guess had no real keyword signal
 * behind it (score_categories_for_text scored the requirement text below
 * NO_SIGNAL_FLOOR - see backend/routes/procurement.py). Never silently
 * adopt a from-nothing guess - force an explicit buyer selection instead.
 *
 * Reuses CategorySelectCell (the Lines step's own category picker) rather
 * than a second dropdown implementation - same rank-never-restrict, full
 * master-list, search-and-tiers machinery. lineItems is always [] here
 * (no lines exist yet at intake) and assessedCategoryCode is always null
 * (nothing to cross-check consistency against yet) - both are safe no-ops
 * in CategorySelectCell for an empty/absent case.
 *
 * The LLM's own (unconfirmed) pick is shown as a labeled suggestion only -
 * NEVER pre-selected into the dropdown's value.
 */
import { useEffect, useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import api from '@/lib/api';
import CategorySelectCell from '@/components/CategorySelectCell';
import { AWAITING_MASTER_UPDATE, categoryNoFitRequestNumber } from '@/lib/categoryNoFit';

const CategoryConfirmRequired = ({
  userRequirement,
  suggestedCategoryCode,
  onConfirm,
  // NO-FIT (F1 / C1b-1b). All three optional and absent by default, so a
  // caller that has not been wired for the declaration renders exactly
  // what this component rendered before.
  noFitSubject = null,
  noFitRecord = null,
  onDeclareNoFit = null,
  // RETIRED INCUMBENT (staleness gate, 2026-09-15). The header code the
  // SERVER reported as no longer active (the advisory / a boundary refusal),
  // or null. Optional and absent by default, so every existing caller renders
  // exactly what it did before.
  retiredCategoryCode = null,
}) => {
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [selected, setSelected] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let isMounted = true;
    api.getMasterCategories('selection')
      .then((response) => {
        if (isMounted) setCategories(response.data || []);
      })
      .catch(() => {
        if (isMounted) setCategories([]);
      })
      .finally(() => {
        if (isMounted) setLoadingCategories(false);
      });
    return () => { isMounted = false; };
  }, []);

  const suggestedCategory = categories.find((c) => c.custom_code === suggestedCategoryCode);

  const handleChange = async (code) => {
    setSelected(code);
    setSaving(true);
    try {
      await onConfirm(code);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="category-confirm-required">
      <div className="flex items-start gap-2 mb-3">
        <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
        {/* TWO REASONS, TWO SENTENCES. The original copy says a category
            could not be DETERMINED, which is false on the no-fit path -
            the buyer determined it perfectly well and the answer was that
            none of them fit. Telling them to "select one" there sends them
            back to a list they have already rejected, which is exactly the
            pressure that manufactures a false category. Same reasoning
            CategoryChangeControl's own docstring gives for not reusing
            this component's framing on ITS path. */}
        {/* THREE REASONS, THREE SENTENCES (a third since 2026-09-15). The
            no-proposal sentence says a category could not be DETERMINED,
            which is false for a retired incumbent: one was determined and
            confirmed, and the master retired it afterwards. Telling that
            buyer their description was unclear blames them for a master
            change they did not cause. The no-fit sentence wins over both. */}
        <p className="text-sm text-amber-900 font-medium" data-testid="category-confirm-headline">
          {noFitRecord
            ? `${AWAITING_MASTER_UPDATE} — this request is with the category administrator.`
            : retiredCategoryCode
              ? 'The category previously confirmed on this requirement has been retired — please reselect.'
              : 'A category could not be determined from your description — please select one.'}
        </p>
      </div>
      {retiredCategoryCode && !noFitRecord && (
        <p className="text-xs text-amber-700 mb-3" data-testid="category-confirm-retired">
          Retired category: {retiredCategoryCode}
        </p>
      )}
      {noFitRecord && (
        <p className="text-xs text-amber-700 mb-3" data-testid="category-confirm-no-fit">
          You requested: {noFitRecord.requested_text}
          {categoryNoFitRequestNumber(noFitSubject)
            ? ` (${categoryNoFitRequestNumber(noFitSubject)})`
            : ''}
          . Selecting a category below withdraws the request and lets you continue.
        </p>
      )}
      {suggestedCategory && !noFitRecord && (
        <p className="text-xs text-amber-700 mb-3" data-testid="category-confirm-suggestion">
          Unconfirmed AI suggestion: <span className="font-medium">{suggestedCategory.category_name} ({suggestedCategory.custom_code})</span>
        </p>
      )}
      <div className="flex items-center gap-2">
        <CategorySelectCell
          value={selected}
          onChange={handleChange}
          description={userRequirement}
          index={0}
          lineItems={[]}
          categories={categories}
          loadingCategories={loadingCategories}
          assessedCategoryCode={null}
          noFitSubject={noFitSubject}
          onDeclareNoFit={onDeclareNoFit}
        />
        {saving && <Loader2 className="w-4 h-4 animate-spin text-amber-600" data-testid="category-confirm-saving" />}
      </div>
    </div>
  );
};

export default CategoryConfirmRequired;
